import { useState, useRef, useEffect } from 'react'
import { Building, ChevronDown, Search, X } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useOrganizations } from '@/hooks'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export function OrgSwitcher() {
  const { user } = useAuth()
  const { selectedOrg, isViewingOtherOrg, selectOrg, clearSelectedOrg } = useOrg()
  const isSuperAdmin = user?.role === 'super_admin'
  const { data: organizations, isLoading } = useOrganizations(isSuperAdmin)

  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  // Reset search when dropdown closes
  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) {
      // Small delay to let the dropdown render
      const timer = setTimeout(() => searchRef.current?.focus(), 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  // Only super_admin can switch orgs
  if (!isSuperAdmin) {
    return null
  }

  const filtered = organizations?.filter((org) =>
    org.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="flex items-center gap-2">
      {isViewingOtherOrg && (
        <Badge variant="secondary" className="gap-1">
          Viewing: {selectedOrg?.name}
          <button
            onClick={clearSelectedOrg}
            className="ml-1 rounded-full hover:bg-muted p-0.5"
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Clear selection</span>
          </button>
        </Badge>
      )}

      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Building className="h-4 w-4" />
            <span className="hidden sm:inline">
              {selectedOrg?.name ?? 'Select Organization'}
            </span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <div className="px-2 pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search organizations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-8 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                onKeyDown={(e) => e.stopPropagation()}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <DropdownMenuItem disabled>Loading...</DropdownMenuItem>
            ) : filtered && filtered.length > 0 ? (
              <>
                {selectedOrg && !search && (
                  <>
                    <DropdownMenuItem onClick={clearSelectedOrg}>
                      Clear Selection
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {filtered.map((org) => (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() => selectOrg(org)}
                    className={selectedOrg?.id === org.id ? 'bg-accent' : ''}
                  >
                    {org.name}
                  </DropdownMenuItem>
                ))}
              </>
            ) : (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                {search ? 'No organizations match your search.' : 'No organizations'}
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
